import capitalize from "lodash-es/capitalize";
import isEmpty from "lodash-es/isEmpty";
import isString from "lodash-es/isString";
import snakeCase from "lodash-es/snakeCase";
import startCase from "lodash-es/startCase";
import toLower from "lodash-es/toLower";
import trim from "lodash-es/trim";
import uniq from "lodash-es/uniq";
import {
  MonitoringPolicyResponseModelV1,
  DataAccessPolicyRoleTemplate,
} from "@/api";
import { WIZARD_ONLY_POLICY_TYPES } from "@/configs/constants";
import services, { AccountTypes } from "@/configs/services";
import I18n from "@/libs/i18n";
import { ClassificationData } from "@/pages/Accounts/Details/Monitoring/MonitoringPolicyContext";
import { IAccountInfo } from "@/pages/Accounts/Registration/accountInfo";
import { getSelectResourceCondition } from "@/pages/Monitoring/data";
import { IAccount } from "@/types/api";
import { isDemoEnvironment } from "./store";

export const titleCase = (text: string) => startCase(toLower(text));

export const isContainsUppercase = (string: string) => /[A-Z]/.test(string);

export const humanize = (str: string) =>
  capitalize(trim(snakeCase(str).replace(/_/g, " ")));

export const statusHumanize = (str: string) =>
  capitalize(trim(str.replace(/_/g, " ")));

export const isSingleTable = (value: string | null) => value === "3";

export const isColumnMaskingPolicy = (value: string | null) => value === "12";

export const isRlsPolicy = (value: string | null) => value === "10";

/**
 * Row- and column-level policies (RLS "10", column masking "12") secure rows and
 * columns of specific objects, so a "secured objects" list is meaningful for them.
 * Object-access policies (schema/warehouse/share/tag grants) have no such list.
 */
export const isRowOrColumnLevelPolicy = (value?: string | null) =>
  isRlsPolicy(value ?? null) || isColumnMaskingPolicy(value ?? null);

/**
 * True for policy types only the new policy wizard can create or edit. The
 * classic single-page form has no step-2 UI, no policySubtype options and no
 * save mapping for them, so it must never be the flow that handles one.
 */
export const isWizardOnlyPolicyType = (value?: string | null) =>
  !!value && WIZARD_ONLY_POLICY_TYPES.has(value);

export const isOutBoundShares = (value: string | null | undefined) =>
  value === "7";

export const isMultiTable = (value: string | null) =>
  value === "2" || value === "5";

export const isAllTable = (value: string | null | undefined) =>
  value === "1" || value === "6" || value === "7";

export const dataSourceTypeId = (value: string | null | undefined) =>
  value === "1" || value === "7";

export const isResourceAccessPolicy = (value: string | null | undefined) =>
  value === "16";

export const isTagSelected = (value: string | null | undefined) =>
  value === "8";

export const isRoleTemplateSelected = (value: string | null | undefined) =>
  value === "9";

// to check if the type is "Database - Multi Schema" or "Schema (All-Tables)"
export const isSchemaAllTableSelected = (value: string | null | undefined) =>
  value === "1";

// to check if the type is "Schema - Multi Objects" or "Schema (Multi-Table)"
export const isSchemaMultiTableSelected = (value: string | null | undefined) =>
  value === "2";

export const isTableOrSchemaOrDatabase = (value: string) =>
  value === "Table" ||
  value === "Schema" ||
  value === "Column" ||
  value === "Database" ||
  value === "View";

export const isDatabaseObjectType = (value: string) => value === "Database";

export const isSchemaObjectType = (value: string) => value === "Schema";

export const isColumnObjectType = (value: string) => value === "Column";

export const isTableObjectType = (value: string) => value === "Table";

export const isViewObjectType = (value: string) => value === "View";

export const isTableName = (value: string) => value === "tableName";

export const isObjectName = (value: string) => value === "objectName";

export const isSchemaName = (value: string) => value === "schemaName";

export const isAccountObjectType = (value: string) => value === "Account";

export const isTypeNamesNeeded = (
  roleTemplateId: string,
  roleTemplates: DataAccessPolicyRoleTemplate[] = [],
) =>
  roleTemplates.filter(
    (roleTemplate: DataAccessPolicyRoleTemplate) =>
      roleTemplate.id === roleTemplateId,
  )?.[0]?.typeNamesNeeded;

// used for object access policy only
export const isMultiDatabases = (
  value: string | null | undefined,
  account: IAccount | undefined,
) =>
  ((account?.type?.toLowerCase() === AccountTypes.Snowflake ||
    account?.type?.toLowerCase() === AccountTypes.Oracle ||
    account?.type?.toLowerCase() === AccountTypes.Databricks ||
    account?.type?.toLowerCase() === AccountTypes.SQLServer) &&
    (value === "8" || value === "1" || value === "9")) ||
  ((account?.type?.toLowerCase() === AccountTypes.Snowflake ||
    account?.type?.toLowerCase() === AccountTypes.Oracle) &&
    value === "2"); // adding multi database only for "Schema - Multi Objects" or "Schema (Multi-Table)"

// used for object access policy only
export const schemaIsMultiDb = (
  value: string | null | undefined,
  account: IAccount | undefined,
) =>
  (account?.type?.toLowerCase() === AccountTypes.Snowflake ||
    account?.type?.toLowerCase() === AccountTypes.Oracle ||
    account?.type?.toLowerCase() === AccountTypes.Databricks ||
    account?.type?.toLowerCase() === AccountTypes.SQLServer) &&
  (value === "8" || value === "1" || value === "9");

export const isShareObjects = (
  value: string | null | undefined,
  account: IAccount | undefined,
) => account?.type?.toLowerCase() === AccountTypes.Snowflake && value === "13";

export const isDremioAccount = (account: IAccount | undefined) =>
  account?.type?.toLowerCase() === AccountTypes.Dremio;

export const isAwsAccount = (account: IAccount | undefined) =>
  account?.type?.toLowerCase() === AccountTypes.AWS ||
  // for demo  bigQuery is treat as redshift for access policy
  account?.type?.toLowerCase() === AccountTypes.BigQuery;

export const isAuroraAccount = (account: IAccount | undefined) =>
  account?.type?.toLowerCase() === AccountTypes.Aurora;

export const isStandardSnowflakeAccount = (account: IAccount | undefined) =>
  account?.type?.toLowerCase() === AccountTypes.Snowflake &&
  account?.edition?.toLowerCase() === "standard";

export const isDataBrickAccount = (account: IAccount | undefined) =>
  account?.type?.toLowerCase() === AccountTypes.Databricks;

export const isOracleAccount = (account: IAccount | undefined) =>
  account?.type?.toLowerCase() === AccountTypes.Oracle;

export const isS3DataLakeAccount = (account: IAccount | undefined) =>
  account?.type?.toLowerCase() === AccountTypes.AmazonS3;

export const isAccountType = (
  type: AccountTypes,
  account: IAccount | IAccountInfo,
) => account?.type?.toLowerCase() === type;

export const isAssumeRoleAccount = (account: IAccount | IAccountInfo) => {
  const type = account?.type?.toLowerCase();
  return services.find(service => service.value === type)?.assumeRole;
};

export const isSnowflake = (account: IAccount | IAccountInfo | undefined) =>
  account?.type?.toLowerCase() === AccountTypes.Snowflake;

export const isSQLServer = (account: IAccount | IAccountInfo | undefined) =>
  account?.type?.toLowerCase() === AccountTypes.SQLServer;

/** SQL Server's native Dynamic Data Masking can't be conditional, so these policies carry no rules. */
export const isSQLServerMaskingPolicy = (
  account: IAccount | IAccountInfo | undefined,
  policyType: string | null | undefined,
) => isSQLServer(account) && isColumnMaskingPolicy(policyType ?? null);

/** Entitlement-by-user reports on these sources take no user/object filters. */
export const isEntitlementFiltersUnsupported = (
  account: { type?: string } | undefined,
) => isSQLServer(account) || isOracleAccount(account);

export const isMySql = (account: IAccount | IAccountInfo | undefined) =>
  account?.type?.toLowerCase() === AccountTypes.MySQL;

export const isPowerBi = (account: IAccount | IAccountInfo | undefined) =>
  account?.type?.toLowerCase() === AccountTypes.PowerBi;

export const isDemo = (): boolean => isDemoEnvironment();

export function paginate<T>(array: T[], pageSize: number, pageNumber: number) {
  return array.slice((pageNumber - 1) * pageSize, pageNumber * pageSize);
}

export const isMonitoringPolicy = (type: string | undefined) =>
  type?.toLowerCase() === MonitoringPolicyTypeStandard.toLowerCase() ||
  type?.toLowerCase() === MonitoringPolicyTypeCustomQuery.toLowerCase() ||
  type?.toLowerCase() === "monitoring" ||
  type?.toLowerCase() === "advanced_monitoring";

export const isStandardMonitoringPolicy = (type: string | undefined) =>
  type?.toLowerCase() === MonitoringPolicyTypeStandard.toLowerCase();

export const isAdvancedMonitoringPolicy = (type: string | undefined) =>
  type?.toLowerCase() === MonitoringPolicyTypeCustomQuery.toLowerCase();

export const isRecommendationPolicy = (type: string | undefined) =>
  type?.toLowerCase() === "recommendation";

// used in monitoring policy
export const getActionsSupportedFields = (
  actionsValue: Array<{ supportedFields?: string | null }> = [],
) =>
  uniq(actionsValue?.map(item => item.supportedFields)).filter(
    item => !isEmpty(item),
  );

export const isWhitelistDataClassificationAdded = (
  row: MonitoringPolicyResponseModelV1,
  value: ClassificationData,
  type: string,
) => {
  const principalTypeIds =
    row.principals?.map(principalItem => principalItem.principalType?.id) || [];

  const hasPrincipalTypeUserRole = principalTypeIds.includes("8");
  const hasPrincipalTypeUserApp = principalTypeIds.includes("5");
  const hasPrincipalTypeUserIp = principalTypeIds.includes("6");
  const hasPrincipalTypeUser = principalTypeIds.includes("2");
  const requiredWhitelistDataType = hasPrincipalTypeUser
    ? "user"
    : hasPrincipalTypeUserApp
      ? "user_app"
      : hasPrincipalTypeUserRole
        ? "user_role"
        : "user_ip";

  return (
    (row.principals &&
      (hasPrincipalTypeUserRole ||
        hasPrincipalTypeUserApp ||
        hasPrincipalTypeUserIp ||
        hasPrincipalTypeUser) &&
      value?.whitelistData.filter(
        item => item.type === requiredWhitelistDataType,
      )?.length === 0 &&
      row.principals?.filter(d => d.listType?.id === "1")?.length > 0) ||
    (row.resourceType?.id === "1" &&
      value?.dataClassificationData?.length === 0 &&
      value.account?.dataClassificationScheme !== "snowflake" &&
      value.account?.dataClassificationScheme !== "databricks" &&
      value.account?.dataClassificationScheme !== "sqlserver" &&
      value.account?.type !== AccountTypes.AmazonS3 &&
      !value.account?.externalCatalog) ||
    ((row.resourceType?.id === "2" ||
      row.resourceType?.id === "3" ||
      row.resourceType?.id === "5" ||
      row.resourceType?.id === "7" ||
      row.resourceType?.id === "8") &&
      (type
        ? // @ts-expect-error type issue
          row.resourceTypeCondition?.id === "1"
        : getSelectResourceCondition(row)?.id === "1") &&
      value?.dataClassificationData?.filter(
        item =>
          item.objectType?.toUpperCase() ===
          (row.resourceType?.id === "2"
            ? "Table"
            : row.resourceType?.id === "3"
              ? "Schema"
              : row.resourceType?.id === "5"
                ? "View"
                : row.resourceType?.id === "7"
                  ? "Object"
                  : "Bucket"
          ).toUpperCase(),
      )?.length === 0 &&
      value.account?.dataClassificationScheme !== "snowflake" &&
      value.account?.dataClassificationScheme !== "databricks" &&
      value.account?.dataClassificationScheme !== "sqlserver" &&
      value.account?.type !== AccountTypes.AmazonS3 &&
      !value.account?.externalCatalog)
  );
};

export const overviewSeriesBarChartLabelContent = (props: { text: string }) => {
  const max = 30;

  const words: string[] = props.text
    .split(" ")
    .map((text: string) =>
      text.length > 30 ? (text.match(/.{1,30}/g)?.join("\n") ?? text) : text,
    );
  const rows: string[] = [];
  let row = "";

  for (let i = 0; i < words.length; i++) {
    if (words[i].length + row.length > max) {
      rows.push(row);
      row = "";
    }
    row += " " + words[i];
  }
  rows.push(row);

  return rows
    .map(item => {
      const spaces = item.length > 20 ? "" : "  ";
      return item + spaces;
    })
    .join("\n");
};

export const kriLabels = {
  login: "LOGIN",
  create: "CREATE",
  create_table: "CREATE TABLE",
  create_table_as_select: "CREATE TABLE AS SELECT",
  alter_table: "ALTER TABLE",
  create_database: "CREATE DATABASE",
  select: "SELECT",
  insert: "INSERT",
  update: "UPDATE",
  delete: "DELETE",
  insert_select: "INSERT SELECT",
  select_into: "SELECT INTO",
  select_into_from: "SELECT INTO FROM",
  select_into_outfile: "SELECT INTO OUTFILE",
  backup_database: "BACKUP DATABASE",
  drop_database: "DROP DATABASE",
  mysqldump: "MYSQL DUMP",
  hr_application: "HR APPLICATION",
  toad_mysql: "TOAD MYSQL",
  employee_application: "EMPLOYEE APPLICATION",
  shadow_it: "Shadow IT",
  overly_granted_access: "Overly Granted Access",
  dark_data: "Dark Data",
  regulatory_compliance: "Regulatory Compliance",
  security_misconfigurations: "Misconfigurations",
  data_exfiltration: "Data Exfiltration",
  classification_type: "classificationType",
  privacy_compliance: "Privacy Compliance",
  object_type: "objectType",
};

export const dataClassificationLabels = {
  schemaName: "Schema Name",
  dbName: "Database Name",
  tableName: "Table Name",
  objectType: "Object Type",
  objectName: "Object Name",
  classificationType: "Classification Type",
};

export const CategoryChartColors = {
  shadow_it: "var(--tlx-color-shadow_it)",
  dark_data: "var(--tlx-color-dark_data)",
  overly_granted_access: "var(--tlx-color-overly_granted_access)",
  regulatory_compliance: "var(--tlx-color-regulatory_compliance)",
  security_misconfigurations: "var(--tlx-color-security_misconfigurations)",
  data_exfiltration: "var(--tlx-color-data_exfiltration)",
  privacy_compliance: "var(--tlx-color-privacy_compliance)",
};

export type Categories = keyof typeof CategoryChartColors;

export const DataAccessPolicyCategories: Categories[] = [
  "dark_data",
  "regulatory_compliance",
  "overly_granted_access",
  "security_misconfigurations",
];

export const MonitoringPolicyTypeStandard: string = "Standard";
export const MonitoringPolicyTypeCustomQuery: string = "Custom Query";
export const MonitoringPolicyTypes: string[] = [
  MonitoringPolicyTypeStandard,
  MonitoringPolicyTypeCustomQuery,
];

export const isAbcAccount = (account?: IAccount) => {
  return (
    services.find(service => service.value === account?.type)?.hasAbacPolicy ||
    (isAwsAccount(account) && isDemo())
  );
};

export const hasNewAccessPolicy = (account?: IAccount) => {
  return services.find(service => service.value === account?.type)
    ?.hasNewAccessPolicy;
};

export interface Validations {
  length: boolean;
  uppercase: boolean;
  lowercase: boolean;
  number: boolean;
  special: boolean;
}

/**
 * Validates a policy name and returns a required-field message when the value is empty after trimming.
 * @param name Optional policy name value.
 * @returns An empty string when valid, otherwise a localized validation message.
 */
export const validatePolicyName = (name?: string | null): string => {
  const normalizedName = String(name ?? "").trim();
  if (!normalizedName) {
    return I18n.t("validation.required");
  }

  return "";
};

export const validatePassword = (password: string) => {
  const validations: Validations = {
    length: password?.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /\d/.test(password),
    special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
  };

  return { validations, isValid: Object.values(validations).every(Boolean) };
};

export const EmailRegex = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/;

export const validateEmail = (value: string) => EmailRegex.test(value);

export const requiredValidator = (value: unknown) => {
  if (value instanceof File) {
    return value.size === 0 ? I18n.t("validation.required") : "";
  }
  const trimmedValue = isString(value) ? trim(value) : value;

  return isEmpty(trimmedValue) ? I18n.t("validation.required") : "";
};

// add new validator to check is a valid url
export const urlValidator = (value: unknown) => {
  if (typeof value !== "string") return I18n.t("validation.invalidUrl");
  const urlPattern = new RegExp(
    "^(https?:\\/\\/)?" + // protocol
      "((([a-zA-Z\\d]([a-zA-Z\\d-]*[a-zA-Z\\d])*)\\.)+[a-zA-Z]{2,}|" + // domain name
      "((\\d{1,3}\\.){3}\\d{1,3}))" + // OR ip (v4) address
      "(\\:\\d+)?(\\/[-a-zA-Z\\d%_.~+]*)*" + // port and path
      "(\\?[;&a-zA-Z\\d%_.~+=-]*)?" + // query string
      "(\\#[-a-zA-Z\\d_]*)?$",
    "i",
  );

  const str = typeof value === "string" ? value : "";
  return !urlPattern.test(str) ? I18n.t("validation.invalidUrl") : undefined;
};

/**
 * Runs validators in order and returns the first message produced. Lets a
 * required field report "Required" for an empty value before being checked
 * for shape — `urlValidator` on its own calls an empty value invalid.
 */
export const composeValidators =
  (...validators: ((value: unknown) => string | undefined)[]) =>
  (value: unknown): string | undefined => {
    for (const validator of validators) {
      const message = validator(value);
      if (message) return message;
    }
    return undefined;
  };

export interface PrincipalByUserType {
  name?: string;
  id?: string;
  userType?: string;
  groupField?: string;
}

export function groupPrincipalsByUserType(
  principals: PrincipalByUserType[],
): PrincipalByUserType[] {
  const ServiceUserTypes = ["SERVICE", "LEGACY_SERVICE"];

  return principals.map(item => {
    const groupField = ServiceUserTypes.includes(item.userType ?? "")
      ? "Service User"
      : "Human User";

    return {
      ...item,
      name: item.name,
      id: item.id || item.name,
      groupField,
    };
  });
}

export const ipAddressValidator = (value: string) => {
  const ipPattern = new RegExp(
    "^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$",
  );
  return !ipPattern.test(value) ? I18n.t("validation.invalidIPAddress") : "";
};
